/**
 * Utility functions for incremental GeoJSON updates
 */

import type { Map as MapboxMap } from "mapbox-gl";
import type { GeoJsonFeature } from "../../api/map/types";

// WeakMap to store current features per map instance
const featuresCache = new WeakMap<MapboxMap, Map<string, GeoJsonFeature<unknown>[]>>();

export interface FeatureDiff<TProperties> {
    added: GeoJsonFeature<TProperties>[];
    updated: GeoJsonFeature<TProperties>[];
    removed: string[]; // IDs of removed features
}

/**
 * Computes the difference between two feature collections
 * Returns features that were added, updated, or removed
 */
export function computeFeatureDiff<TProperties>(
    oldFeatures: GeoJsonFeature<TProperties>[],
    newFeatures: GeoJsonFeature<TProperties>[],
    getId: (feature: GeoJsonFeature<TProperties>) => string | number | undefined
): FeatureDiff<TProperties> {
    const oldMap = new Map<string | number, GeoJsonFeature<TProperties>>();
    const newMap = new Map<string | number, GeoJsonFeature<TProperties>>();

    // Build maps of old and new features by ID
    oldFeatures.forEach((feature) => {
        const id = getId(feature);
        if (id !== undefined) {
            oldMap.set(id, feature);
        }
    });

    newFeatures.forEach((feature) => {
        const id = getId(feature);
        if (id !== undefined) {
            newMap.set(id, feature);
        }
    });

    const added: GeoJsonFeature<TProperties>[] = [];
    const updated: GeoJsonFeature<TProperties>[] = [];
    const removed: string[] = [];

    // Find added and updated features
    newMap.forEach((newFeature, id) => {
        const oldFeature = oldMap.get(id);
        if (!oldFeature) {
            added.push(newFeature);
        } else {
            // Check if feature was actually updated (simple deep comparison)
            if (hasFeatureChanged(oldFeature, newFeature)) {
                updated.push(newFeature);
            }
        }
    });

    // Find removed features
    oldMap.forEach((_, id) => {
        if (!newMap.has(id)) {
            removed.push(String(id));
        }
    });

    return { added, updated, removed };
}

/**
 * Checks if a feature has changed by comparing properties and geometry
 */
function hasFeatureChanged<TProperties>(
    oldFeature: GeoJsonFeature<TProperties>,
    newFeature: GeoJsonFeature<TProperties>
): boolean {
    // Compare geometry
    if (JSON.stringify(oldFeature.geometry) !== JSON.stringify(newFeature.geometry)) {
        return true;
    }

    // Compare properties (shallow comparison for performance)
    const oldProps = oldFeature.properties;
    const newProps = newFeature.properties;

    const oldKeys = Object.keys(oldProps as object);
    const newKeys = Object.keys(newProps as object);

    if (oldKeys.length !== newKeys.length) {
        return true;
    }

    for (const key of oldKeys) {
        if (oldProps[key as keyof TProperties] !== newProps[key as keyof TProperties]) {
            return true;
        }
    }

    return false;
}

/**
 * Gets cached features for a specific source in a map instance
 */
export function getCachedFeatures<TProperties>(
    map: MapboxMap,
    sourceId: string
): GeoJsonFeature<TProperties>[] {
    const mapCache = featuresCache.get(map);
    if (!mapCache) {
        return [];
    }
    return (mapCache.get(sourceId) ?? []) as GeoJsonFeature<TProperties>[];
}

/**
 * Caches features for a specific source in a map instance
 */
export function setCachedFeatures<TProperties>(
    map: MapboxMap,
    sourceId: string,
    features: GeoJsonFeature<TProperties>[]
): void {
    let mapCache = featuresCache.get(map);
    if (!mapCache) {
        mapCache = new Map();
        featuresCache.set(map, mapCache);
    }
    mapCache.set(sourceId, features as GeoJsonFeature<unknown>[]);
}

/**
 * Applies incremental updates to a Mapbox GeoJSON source
 * This minimizes re-rendering by only updating changed features
 *
 * @param keepExistingFeatures - If true, keeps all existing features and only adds/updates new ones.
 *                                Useful for roads where we want to accumulate features when zooming out.
 */
export function applyIncrementalUpdate<TProperties>(
    map: MapboxMap,
    source: mapboxgl.GeoJSONSource,
    sourceId: string,
    newFeatures: GeoJsonFeature<TProperties>[],
    getId: (feature: GeoJsonFeature<TProperties>) => string | number | undefined,
    keepExistingFeatures: boolean = false
): void {
    const currentFeatures = getCachedFeatures<TProperties>(map, sourceId);

    // If keepExistingFeatures is true, always merge (for roads)
    if (keepExistingFeatures) {
        // Merge strategy: keep all existing features and add/update with new ones
        const mergedFeatures = new Map<string | number, GeoJsonFeature<TProperties>>();

        // Add all existing features from cache
        currentFeatures.forEach((feature) => {
            const id = getId(feature);
            if (id !== undefined) {
                mergedFeatures.set(id, feature);
            }
        });

        // Add/update with new features (will overwrite existing if same ID)
        newFeatures.forEach((feature) => {
            const id = getId(feature);
            if (id !== undefined) {
                mergedFeatures.set(id, feature);
            }
        });

        const mergedArray = Array.from(mergedFeatures.values());
        source.setData({
            type: "FeatureCollection",
            features: mergedArray as any
        });
        setCachedFeatures(map, sourceId, mergedArray as GeoJsonFeature<TProperties>[]);
        return;
    }

    // Normal incremental update (for non-road layers)
    const diff = computeFeatureDiff(currentFeatures, newFeatures, getId);

    // If too many changes, do a full update (more efficient)
    const changeRatio = (diff.added.length + diff.updated.length + diff.removed.length) / Math.max(newFeatures.length, 1);
    if (changeRatio > 0.5 || currentFeatures.length === 0) {
        // More than 50% changed or initial load - full update is more efficient
        source.setData({
            type: "FeatureCollection",
            features: newFeatures as any
        });
        setCachedFeatures(map, sourceId, newFeatures);
        return;
    }

    // Build updated feature map from cached features
    const updatedFeatureMap = new Map<string | number, GeoJsonFeature<TProperties>>();

    // Keep existing features from cache (only if not marked as removed)
    currentFeatures.forEach((feature) => {
        const id = getId(feature);
        if (id !== undefined && !diff.removed.includes(String(id))) {
            updatedFeatureMap.set(id, feature);
        }
    });

    // Add new features (will overwrite existing if already in map)
    diff.added.forEach((feature) => {
        const id = getId(feature);
        if (id !== undefined) {
            updatedFeatureMap.set(id, feature);
        }
    });

    // Update changed features (will overwrite existing)
    diff.updated.forEach((feature) => {
        const id = getId(feature);
        if (id !== undefined) {
            updatedFeatureMap.set(id, feature);
        }
    });

    // Convert map back to array
    const updatedFeatures = Array.from(updatedFeatureMap.values());

    // Apply update
    source.setData({
        type: "FeatureCollection",
        features: updatedFeatures as any
    });

    // Update cache with new features
    setCachedFeatures(map, sourceId, newFeatures);
}